<template>
  <div>
    <panel-block id="pn-default-date-value" v-if="isFormElement">
      <panel-item>
        <date-default-value-selector
          :datetime-mode="datetimeMode"
          :parent="model"
          field-name="default_datetime"
          :allow-formula="allowFormula"
          :dynamic-options="formulaDynamicFieldsOptions"
          :date-picker-props="{min: minDatetime, max: maxDatetime}"
        >
          <template #label>
            <slot name="minLabel"></slot>
          </template>
        </date-default-value-selector>
      </panel-item>
    </panel-block>

    <panel-block id="pn-collect-date-time">
      <panel-item>
        <row>
          <prefix v-if="isLockable">
            <lock
              :configurable="isLockConfigurable"
              v-model="model[lockProperty]"
            ></lock>
          </prefix>
          <column>
            <label for="collectDate">
              Collect date/time?
              <slot name="modeLabel"></slot>
            </label>
          </column>
        </row>
        <a-select
          ref="modeComponent"
          @input="setDatetimeMode"
          :options="datetimeModeVOptions"
          id="collectDate"
          compress
          :disabled="disabled"
          :value="model.datetime_mode"
        ></a-select>
      </panel-item>
    </panel-block>

    <panel-block id="pn-time-format"
                 v-if="datetimeMode !== DatetimeMode.Date">
      <panel-item>
        <label for="timeFormat">
          Time Format
          <slot name="timeFormatLabel"></slot>
        </label>
        <a-select
          v-model="model.time_format"
          :options="timeFormatVOptions"
          id="timeFormat"
          compress
          :disabled="disabled"
        ></a-select>
      </panel-item>
    </panel-block>

    <panel-block id="pn-date-time-type" panel-group>
      <panel-item>
        <date-min-max-value-selector
          :datetime-mode="datetimeMode"
          :parent="model"
          field-name="min_datetime"
          display-name="Minimum"
          :date-picker-props="{max: maxDatetime}"
          :custom-min-max-type-options="customMinMaxTypeOptions"
          :allow-formula="allowFormula"
          :dynamic-options="formulaDynamicFieldsOptions"
        >
          <template #label>
            <slot name="minLabel"></slot>
          </template>
        </date-min-max-value-selector>
      </panel-item>

      <panel-item>
        <date-min-max-value-selector
          :datetime-mode="datetimeMode"
          :parent="model"
          field-name="max_datetime"
          display-name="Maximum"
          :date-picker-props="{min: minDatetime}"
          :custom-min-max-type-options="customMinMaxTypeOptions"
          :allow-formula="allowFormula"
          :dynamic-options="formulaDynamicFieldsOptions"
        >
          <template #label>
            <slot name="maxLabel"></slot>
          </template>
        </date-min-max-value-selector>
      </panel-item>
    </panel-block>
  </div>
</template>

<script lang="ts">
  import Vue from "vue";
  import {IDefaultVOption, IVOptionSetLocal} from "../interfaces/vSelectInterfaces";
  import {DatetimeMode, DatetimeModeDisplay} from "../models/datetimeMode";
  import {TimeFormat} from "../models/timeFormat";
  import {DatetimeType} from "../models/datetimeType";
  import {IDateMinMaxable} from "../interfaces/propertyInterfaces";
  import disabledMixin from "../mixins/disabledMixin";
  import Lock from "./lock.vue";
  import DateMinMaxValueSelector from "./date-min-max-value-selector.vue";
  import DateDefaultValueSelector from "./date-default-value-selector.vue";
  import {IDynamicFieldsOptions} from "~/interfaces/dynamicFieldsInterfaces/dynamicOptionsInterfaces";
  import FormElement from "../../formability/form_builder/models/formElements/formElement";

  const DEFAULT_PLACEHOLDERS: string[] = ['MM/DD/YYYY HH:MM', 'MM/DD/YYYY', 'HH:MM'];

  export default Vue.extend({
    components: {
      Lock,
      DateMinMaxValueSelector,
      DateDefaultValueSelector,
    },
    mixins: [
      disabledMixin,
    ],
    props: {
      model: {
        type: Object as () => IDateMinMaxable
      },
      isLockable: {
        type: Boolean,
        default: false,
      },
      isLockConfigurable: {
        type: Boolean,
        default: false,
      },
      lockProperty: {
        type: String,
      },
      customMinMaxTypeOptions: {
        type: Array as () => IDefaultVOption[],
      },
      allowFormula: {
        type: Boolean,
        default: false
      },
      formulaDynamicFieldsOptions: {
        type: Array as () => IDynamicFieldsOptions[],
      },
    },
    data: function () {
      return {
        DatetimeMode,
        DatetimeType,
        originalSelection: this.model.datetime_mode
      };
    },
    computed: {
      datetimeMode: {
        get(): DatetimeMode {
          return this.model.datetime_mode;
        },
        set(value: DatetimeMode) {

          if (this.$listeners['mode-update']) {
            this.$emit('mode-update', value, this.originalSelection);
          }
           else {
            this.model.datetime_mode = value;
          }
        }
      },
      isFormElement(): boolean {
        return this.model instanceof FormElement;
      },
      datetimeModeVOptions(): IVOptionSetLocal {
        let options = [
          {label: DatetimeModeDisplay[DatetimeMode.Date], value: DatetimeMode.Date},
          {label: DatetimeModeDisplay[DatetimeMode.Time], value: DatetimeMode.Time},
          {label: DatetimeModeDisplay[DatetimeMode.Datetime], value: DatetimeMode.Datetime}
        ];
        return {options};
      },
      timeFormatVOptions(): IVOptionSetLocal {
        let options = [
          {label: '12hr', value: TimeFormat.TwelveHour},
          {label: '24hr', value: TimeFormat.TwentyFourHour}
        ];
        return {options};
      },
      minDatetimeType: {
        get(): DatetimeType {
          return this.model.min_datetime_type;
        },
        set(value: DatetimeType) {
          if (value !== DatetimeType.Custom) {
            this.model.min_datetime = '';
          }
          this.model.min_datetime_type = value;
        }
      },
      minDatetime: {
        get(): any {
          if (this.model.min_datetime) {
            return this.model.min_datetime;
          }
        },
        set(value: any) {
          this.model.min_datetime = value;
        }
      },
      maxDatetimeType: {
        get(): DatetimeType {
          return this.model.max_datetime_type;
        },
        set(value: DatetimeType) {
          if (value !== DatetimeType.Custom) {
            this.model.max_datetime = '';
          }
          this.model.max_datetime_type = value;
        }
      },
      maxDatetime: {
        get(): any {
          if (this.model.max_datetime) {
            return this.model.max_datetime;
          }
        },
        set(value: any) {
          this.model.max_datetime = value;
        },
      },
    },
    methods: {
      dateRangeDisplay(preface): string {
        switch (this.datetimeMode) {
          case DatetimeMode.Date:
            return `${preface} Date`;
          case DatetimeMode.Time:
            return `${preface} Time`;
          case DatetimeMode.Datetime:
            return `${preface} Date and Time`;
        }
      },
      cancelModeSelection(): void {
        this.model.datetime_mode = this.originalSelection;
        this.model._track = true;
      },
      setDatetimeMode(value) {
        if (this.model.hasConditionalRuleDependencies) {
          this.model._track = false;
        }
        this.originalSelection = this.model.datetime_mode;
        this.model.datetime_mode = value;
        this.datetimeMode = value;
      },
    },
    watch: {
      datetimeMode(newVal) {
        if (this.model instanceof FormElement) {
          this.model.setPlaceholderText(newVal);
        }
      }
    },
  });
</script>
